<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { deletePostAction, getPostAction } from '@/api/functions/settings/blogs-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const route = useRoute()
const router = useRouter()
const toast = useToast()

const slug = route.params.id as string
const showDeleteModal = ref(false)

const { data: postData, refetch } = useQueryApi({
  queryKey: ['post-details', slug],
  queryFn: () => getPostAction(slug),
  staleTime: 1000 * 60 * 2,
})

const blog = computed(() => postData.value?.data ?? null)

const goToBlogEdit = (s: string | undefined) => {
  if (s) {
    router.push(`/settings/blog/${s}/edit`)
  }
}

const deleteMutation = useMutationApi({
  mutationFn: (id: string) => deletePostAction([id]),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'بلاگ با موفقیت حذف شد')
    router.push('/settings/blog')
    showDeleteModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDelteBlog = () => {
  if (!blog?.value?.id) return
  deleteMutation.mutate(blog.value.id)
}
</script>

<template>
  <div class="mb-9">

    <ConfirmModal v-model="showDeleteModal" title="حذف کردن مطالب وبلاگ"
      message="آیا از حذف کردن این مطالب اطمینان دارید؟" confirm-text="بله حذف شود" cancel-text="بازگشت"
      @confirm="handleDelteBlog" @cancel="showDeleteModal = false" />

    <router-link to="/settings/blog"
      class="w-fit mb-6 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
      <img src="/svg/back-icon.svg" class="me-1" width="22" />
      <span>بازگشت</span>
    </router-link>

    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-0 mb-9">
      <div class="text-[#1b325a] text-[22px]">
        جزئیات مطلب: {{ blog?.title || '' }}
      </div>

      <div class="w-full lg:w-auto flex flex-row items-center gap-2">
        <Button :isAdmin="true" @click="goToBlogEdit(blog?.slug)"
          class="w-1/2 lg:w-auto px-2 rounded-[10px] h-10! py-0 text-[13px] font-normal">
          <span class="flex items-center justify-center gap-2">
            <img width="20" src="/svg/edit-icon.svg" />
            ویرایش
          </span>
        </Button>

        <Button @click="showDeleteModal = true" :isAdmin="true" variant="outline"
          class="w-1/2 lg:w-auto px-2 border-2 rounded-[10px] h-10! py-0 text-[13px] font-normal">
          حذف
        </Button>

        <Button :isAdmin="true" @click="refetch()"
          class="w-1/2 lg:w-auto px-2 rounded-[10px] h-10! py-0 text-[13px] font-normal">
          بروزرسانی
        </Button>
      </div>
    </div>

    <div class="w-full flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-24 p-[26px] bg-white rounded-xl">
      <span class="text-[#1b325a] text-sm">عنوان مطلب</span>
      <p class="text-[#000000de] text-sm">{{ blog?.title }}</p>
    </div>

    <div class="w-full flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-24 p-[26px] bg-white rounded-xl my-4">
      <span class="text-[#1b325a] text-sm">تصویر نمایه</span>
      <img :src="blog?.thumbnail" class="w-full lg:w-[360px] h-[211px] rounded-xl border object-cover" />
    </div>

    <div class="w-full flex flex-col p-[26px] bg-white rounded-xl">
      <span class="text-[#1b325a] text-sm">محتوا</span>


      <div class="text-[#000000de] text-sm mt-9 leading-6 text-justify" v-html="blog?.content" />

    </div>
  </div>
</template>
